import React, { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import axios from "axios";
import config from "../../config";
import { ToastContainer, toast } from "react-toastify";
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar';
import { usePageSave } from '../../contexts/PageSaveContext';
// Replace your section options with the new data
const sectionOptions = [
  {
    code: "AGRI",
    description: "Agriculture Income (<= Rs.5000)"
  },
  {
    code: "10(10BC)",
    description: "Sec 10(10BC) - Compensation from Govt./local authority for disaster"
  },
  {
    code: "10(10D)",
    description: "Sec 10(10D) - Sum received under a life insurance policy (conditions apply)"
  },
  {
    code: "10(11)",
    description: "Sec 10(11) - Statutory Provident Fund received"
  },
  {
    code: "10(12)",
    description: "Sec 10(12) - Recognised Provident Fund received"
  },
  {
    code: "10(12C)",
    description: "Sec 10(12C) - Payment from Agniveer Corpus Fund to Agnipath enrollee or nominee"
  },
  {
    code: "10(13)",
    description: "Sec 10(13) - Approved superannuation fund received"
  },
  {
    code: "10(16)",
    description: "Sec 10(16) - Scholarships granted to meet the cost of education"
  },
  {
    code: "10(17)",
    description: "Sec 10(17) - Allowance received by MP/MLA/MLC"
  },
  {
    code: "10(17A)",
    description: "Sec 10(17A) - Award instituted by Government"
  },
  {
    code: "10(18)",
    description: "Sec 10(18) - Pension for gallantry award recipients (e.g., Param Vir Chakra)"
  },
  {
    code: "DMDP",
    description: "Defense medical disability pension"
  },
  {
    code: "10(19)",
    description: "Sec 10(19) - Armed Forces Family Pension (death during operational duty)"
  },
  {
    code: "10(26)",
    description: "Sec 10(26) - Income exempt under Sec 10(26)"
  },
  {
    code: "10(26AAA)",
    description: "Sec 10(26AAA) - Income exempt under Sec 10(26AAA)"
  },
  {
    code: "OTH",
    description: "Any Other"
  }
];


export default function ExemptIncomeSection() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [panNo, setPanCardNumber] = useState(localStorage.getItem("PAN_NO"))
  const [assessmentYear, setAssessmentYear] = useState(localStorage.getItem("YEAR"))
  const [validationErrors, setValidationErrors] = useState({})

  // Fetch data on component mount or when panNo/assessmentYear changes
  useEffect(() => {
    const fetchExemptIncome = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${config.apiUrl}/income/exempt/${panNo}/${assessmentYear}`
        );

        if (response.data.exemptIncomeDto && response.data.exemptIncomeDto.length > 0) {
          setRows(response.data.exemptIncomeDto.map(item => ({
            id: item.exemptIncomeId || Date.now(),
            section: item.sectionCode,
            description: item.description ||
              sectionOptions.find(opt => opt.code === item.sectionCode)?.description || "",
            amount: item.amount.toString(),
            exemptIncomeId: item.exemptIncomeId,
            OthNatOfInc: item.OthNatOfInc || "" // Add this line
          })));
        } else {
          // Add empty row if no data exists
          setRows([{ id: Date.now(), section: "", amount: "" }]);
        }
      } catch (error) {
        toast.error("Failed to fetch exempt income data");
        console.error("Error fetching exempt income:", error);
        // Add empty row on error
        setRows([{ id: Date.now(), section: "", amount: "" }]);
      } finally {
        setIsLoading(false);
      }
    };

    if (panNo && assessmentYear) {
      fetchExemptIncome();
    }
  }, [panNo, assessmentYear]);

  const handleAddRow = (index) => {
    const newRow = { id: Date.now() + index, section: "", amount: "" };
    const newRows = [...rows];
    newRows.splice(index + 1, 0, newRow);
    setRows(newRows);
  };

  const handleRemoveRow = async (id) => {
    const rowToDelete = rows.find(row => row.id === id);

    // If this is an existing record with an exemptIncomeId, call DELETE API
    if (rowToDelete?.exemptIncomeId) {
      try {
        await axios.delete(
          `${config.apiUrl}/income/exempt/${rowToDelete.exemptIncomeId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }

    // Remove from UI
    setRows(prev => prev.filter(row => row.id !== id));

    // If last row was deleted, add a new empty one
    if (rows.length === 1) {
      setRows([{ id: Date.now(), section: "", amount: "" }]);
    }
  };

  const handleChange = (id, field, value) => {
    markPageAsUnsaved('exempt-income')
    setRows(prev =>
      prev.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );

    // Clear validation errors when fields become valid
    if (field === "section") {
      if (value === "AGRI") {
        clearValidationError(`mandatory_description_${id}`);
      } else if (value === "OTH") {
        clearValidationError(`agriculture_amount_limit_${id}`);
      } else {
        clearValidationError(`agriculture_amount_limit_${id}`);
        clearValidationError(`mandatory_description_${id}`);
      }
    } else if (field === "amount") {
      const row = rows.find(r => r.id === id);
      if (row && row.section === "AGRI") {
        const amount = parseFloat(value) || 0;
        if (amount <= 5000) {
          clearValidationError(`agriculture_amount_limit_${id}`);
        }
      }
    } else if (field === "OthNatOfInc") {
      if (value && value.trim() !== "") {
        clearValidationError(`mandatory_description_${id}`);
      }
    }
  };

  const saveExemptIncome = async () => {
    if (!panNo || !assessmentYear) {
      toast.error("PAN Number or Assessment Year is missing");
      return;
    }

    // Validate exempt income before saving
    if (!validateExemptIncome()) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        exemptIncomeDto: rows.map(row => ({
          exemptIncomeId: row.exemptIncomeId || null,
          sectionCode: row.section,
          description: sectionOptions.find(opt => opt.code === row.section)?.description || "",
          OthNatOfInc: row.section === "OTH" ? row.OthNatOfInc : "",
          amount: parseFloat(row.amount) || 0,
          OthNatOfInc: row.OthNatOfInc || "" // Add this line
        }))
      };

      const response = await axios.patch(
        `${config.apiUrl}/income/exempt/${panNo}/${assessmentYear}`,
        payload
      );

      // Update rows with any new IDs from the server
      if (response.data.exemptIncomeDto) {
        setRows(rows.map((row, index) => ({
          ...row,
          exemptIncomeId: response.data.exemptIncomeDto[index]?.exemptIncomeId || row.exemptIncomeId
        })))
      }

      // Make GET call to fetch updated data after saving
      try {
        const getResponse = await axios.get(
          `${config.apiUrl}/income/exempt/${panNo}/${assessmentYear}`
        );

        if (getResponse.data.exemptIncomeDto && getResponse.data.exemptIncomeDto.length > 0) {
          setRows(getResponse.data.exemptIncomeDto.map(item => ({
            id: item.exemptIncomeId || Date.now(),
            section: item.sectionCode,
            description: item.description ||
              sectionOptions.find(opt => opt.code === item.sectionCode)?.description || "",
            amount: item.amount.toString(),
            exemptIncomeId: item.exemptIncomeId,
            OthNatOfInc: item.OthNatOfInc || ""
          })));
        } else {
          // Add empty row if no data exists
          setRows([{ id: Date.now(), section: "", amount: "" }]);
        }
      } catch (getError) {
        console.error("Error fetching exempt income after save:", getError);
      }

      const saved = markPageAsSaved('exempt-income')
      if (saved) {
        toast.success("Exempt income saved successfully");
      } else {
        toast.error("Please save your profile first before saving exempt income details!");
      }
    } catch (error) {
      toast.error("Failed to save exempt income");
      console.error("Error saving exempt income:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const totalAmount = rows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0
  );

  // Validation function for exempt income
  const validateExemptIncome = () => {
    const errors = {};
    
    rows.forEach((row, index) => {
      // Validate agriculture income amount limit
      if (row.section === "AGRI") {
        const amount = parseFloat(row.amount) || 0;
        if (amount > 5000) {
          errors[`agriculture_amount_limit_${row.id}`] = `Agriculture income amount (₹${amount.toFixed(2)}) cannot exceed ₹5,000`;
        }
      }
      
      // Validate mandatory description for "Any Other"
      if (row.section === "OTH") {
        if (!row.OthNatOfInc || row.OthNatOfInc.trim() === "") {
          errors[`mandatory_description_${row.id}`] = "Description is mandatory when 'Any Other' is selected";
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clear validation errors for a specific field
  const clearValidationError = (errorKey) => {
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const getFilteredOptions = (currentId) => {
    const currentRow = rows.find(r => r.id === currentId);
    const selectedCodes = rows
      .filter(r => r.id !== currentId)
      .map(r => r.section)
      // Only filter out non-OTH selections
      .filter(code => code !== "OTH");

    return sectionOptions.filter(opt =>
      !selectedCodes.includes(opt.code) || opt.code === "OTH"
    );
  };


  const [exemptIncomeOptionList, setExemptIncomeOptionList] = useState([])

  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`);
      const data = response.data;

      if (data && data.codes?.length > 0) {
        setDataFn(data.codes);
      } else {
        setDataFn([]); // empty array if no data
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error);
      toast.error(`Failed to fetch ${codeType}.`);
    }
  };

  useEffect(() => {
    getAllCodeList("NatureDesc", setExemptIncomeOptionList);
    ;
  }, [])

  if (isLoading) {
    return <div className="p-4 text-center">Loading exempt income data...</div>;
  }

  return (
    <div className="bg-[#F4F9FC] p-4 h-full rounded-xl mx-auto">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="grid grid-cols-1 md:grid-cols-12 font-semibold text-gray-600 border-b pb-2">
        <div className="md:col-span-5">Nature</div>
        <div className="md:col-span-5">Amount</div>
        <div className="md:col-span-2 text-center">Actions</div>
      </div>

         {rows.map((row, index) => {
        const availableOptions = getFilteredOptions(row.id);
        const showDescriptionField = row.section === "OTH";

        return (
          <div key={row.id} className="space-y-2 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              {/* Nature dropdown */}
              <div className={`${showDescriptionField ? 'md:col-span-3' : 'md:col-span-5'}`}>
                <select
                  className="w-full p-2 rounded-md border border-gray-300 bg-white text-gray-600 focus:outline-none"
                  value={row.section}
                  onChange={(e) => {
                    handleChange(row.id, "section", e.target.value);
                    // Clear description when switching away from OTH
                    if (e.target.value !== "OTH") {
                      handleChange(row.id, "OthNatOfInc", "");
                    }
                  }}
                >
                  <option value="">Select Nature</option>
                  {availableOptions.map(opt => (
                    <option key={opt.code} value={opt.code}>
                      {opt.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description field (only shown when OTH is selected) */}
              {showDescriptionField && (
                <div className="md:col-span-2">
                  <input
                    type="text"
                    className="w-full p-2 rounded-md border border-gray-300 text-gray-700 focus:outline-none"
                    placeholder="Enter description"
                    value={row.OthNatOfInc || ""}
                    onChange={(e) => handleChange(row.id, "OthNatOfInc", e.target.value)}
                  />
                </div>
              )}

              {/* Amount field */}
              <div className={`${showDescriptionField ? 'md:col-span-5' : 'md:col-span-5'}`}>
                <input
                  type="text"
                  className="w-full p-2 rounded-md border border-gray-300 text-gray-700 focus:outline-none text-right"
                  placeholder="0.00"
                  value={row.amount}
                  onChange={(e) => {
                    // Only allow numbers (0-9) and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                    handleChange(row.id, "amount", numericValue.toString());
                  }}
                />
              </div>

              {/* Action buttons */}
              <div className="md:col-span-2 flex justify-center gap-2">
                <button
                  onClick={() => handleAddRow(index)}
                  className="p-2 bg-[#00A3FF] hover:bg-[#0090E0] text-white rounded-md"
                >
                  <Plus size={16} />
                </button>
                {rows.length > 1 && (
                  <button
                    onClick={() => handleRemoveRow(row.id)}
                    className="p-2 bg-[#FF4D4F] hover:bg-[#e04344] text-white rounded-md"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 pt-4 items-center">
        <div className="md:col-span-5 font-semibold text-gray-700">
          Total Exempt Income
        </div>
        <div className="md:col-span-5">
          <input
            type="text"
            className="w-full p-2 rounded-md bg-gray-100 text-right text-gray-700"
            value={totalAmount.toFixed(2)}
            readOnly
          />
        </div>
        <div className="md:col-span-2">
          <button
            onClick={saveExemptIncome}
            disabled={isSaving}
            className={`w-full p-2 rounded-md text-white ${isSaving ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'
              }`}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      
      {/* Validation Error Sidebar */}
      <ValidationErrorSidebar
        errors={validationErrors}
        isVisible={true}
        onFieldClick={(fieldKey) => {
          // Handle field click to focus on the specific field
          console.log("Field clicked:", fieldKey);
        }}
        title="Exempt Income Validation Errors"
      />
    </div>
  );
}
